import React, { useContext } from "react";
import {
    Grid,
    GridRow,
    GridColumn,
    Stack,
    ClearFiltersButton,
    MultiselectSearchDropdown,
    ListTable,
    mapToDropdownOption,
    DropdownMultiselect,
    DatePicker,
    ContentSection,
    ButtonRenderProps
} from "sprint-common-web";

import { CloudAssetOrigin } from "../../../common/types/AssetOrigin";
import { AssetStatus } from "../../../common/types/AssetStatus";
import { AssetType } from "../../../common/types/AssetType";
import { RecordType } from "../../../common/types/RecordType";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { mapEnumToDropdownOptions } from "../../../common/utils/mapEnumToDropdownOptions";
import { AssetsWarning } from "../common/AssetsWarning";
import {
    EditAssetListingColumnsConfigButton,
    EditAssetListingColumnsConfigButtonProps
} from "../common/config/EditFileRecordListingColumnsConfigButton";
import { createAssetListTableColumns } from "../common/createAssetListTableColumns";
import { renderAssetRow } from "../common/renderAssetRow";
import { SingleMovieContext } from "./context/SingleMovieContext";
import { useMovieAssetsList } from "./hooks/useMovieAssetsList";
import { useMovieAssetsListFilters } from "./hooks/useMovieAssetsListFilters";
import { MovieAssetsListBreadcrumbs } from "./MovieAssetsListBreadcrumbs";

export const MovieAssetsList: React.FC = () => {
    const { movie, isLoading: isContextLoading, listingConfig, saveListingConfig } = useContext(SingleMovieContext);

    const {
        filters,
        versionDetails,
        trailersOptions,
        setStatuses,
        setPage,
        setPageSize,
        setSort,
        clearFilters,
        setVersionTypes,
        setDistributors,
        setAssetTypes,
        setAssetOrigins,
        setAssetRecordTypes,
        setTrailers,
        setCreatedDateFrom,
        setCreatedDateTo,
        setUpdatedDateFrom,
        setUpdatedDateTo
    } = useMovieAssetsListFilters();

    const { items, totalItems, isLoading } = useMovieAssetsList({ filters });

    const columns = createAssetListTableColumns({ config: listingConfig });

    const configButton: ButtonRenderProps<EditAssetListingColumnsConfigButtonProps> = {
        render: EditAssetListingColumnsConfigButton,
        listingConfig,
        saveListingConfig,
        pageSize: filters.pageSize,
        setPageSize: setPageSize
    };

    return (
        <>
            <PageHeader>
                <MovieAssetsListBreadcrumbs
                    movieTitle={movie.title}
                    subtype={movie.subtype}
                    isLoading={isContextLoading}
                />
            </PageHeader>
            <AssetsWarning items={items} />
            <ContentSection header="Movie assets" buttons={[configButton]}>
                <Grid>
                    <GridRow>
                        <GridColumn sm={2}>
                            <MultiselectSearchDropdown
                                placeholder="Distributors"
                                options={versionDetails.distributors.map((distributor) =>
                                    mapToDropdownOption(distributor)
                                )}
                                selectedKeys={filters.distributorIds}
                                onChange={setDistributors}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <MultiselectSearchDropdown
                                placeholder="Version types"
                                options={versionDetails.versionTypes.map((versionType) =>
                                    mapToDropdownOption(versionType)
                                )}
                                selectedKeys={filters.versionTypeIds}
                                onChange={setVersionTypes}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Statuses"
                                selectedKeys={filters.statusIds?.map((status) => status.toString())}
                                onChange={setStatuses}
                                options={mapEnumToDropdownOptions(AssetStatus)}
                                testId="movie-asset-status-filter"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Asset type"
                                selectedKeys={filters.types?.map((t) => t.toString())}
                                onChange={setAssetTypes}
                                options={mapEnumToDropdownOptions(AssetType)}
                                testId="movie-asset-types-filter"
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Asset record type"
                                selectedKeys={filters.recordTypes?.map((t) => t.toString())}
                                onChange={setAssetRecordTypes}
                                options={mapEnumToDropdownOptions(RecordType)}
                                testId="movie-asset-record-types-filter"
                            />
                        </GridColumn>

                        <GridColumn sm={1}>
                            <Stack horizontalAlign="end">
                                <ClearFiltersButton isLabeled={true} onClear={clearFilters} />
                            </Stack>
                        </GridColumn>
                    </GridRow>
                    <GridRow style={{ paddingTop: 10 }}>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Asset origin"
                                selectedKeys={filters.origins?.map((t) => t.toString())}
                                onChange={setAssetOrigins}
                                options={mapEnumToDropdownOptions(CloudAssetOrigin)}
                                testId="movie-asset-origins-filter"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Trailers"
                                selectedKeys={filters.trailers}
                                onChange={setTrailers}
                                options={trailersOptions}
                                testId="assets-filters-trailers"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DatePicker
                                testId="assets-filters-created-date-from"
                                placeholder="Created date from"
                                value={filters.createdDateFrom}
                                onDateSelect={setCreatedDateFrom}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DatePicker
                                testId="assets-filters-created-date-to"
                                placeholder="Created date to"
                                value={filters.createdDateTo}
                                onDateSelect={setCreatedDateTo}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DatePicker
                                testId="assets-filters-updated-date-from"
                                placeholder="Updated date from"
                                value={filters.updatedDateFrom}
                                onDateSelect={setUpdatedDateFrom}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DatePicker
                                testId="assets-filters-updated-date-to"
                                placeholder="Updated date to"
                                value={filters.updatedDateTo}
                                onDateSelect={setUpdatedDateTo}
                            />
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn>
                            <ListTable
                                columns={columns}
                                items={items}
                                pagination={{
                                    ...filters,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                onRenderRow={renderAssetRow}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSortChange={setSort}
                                isLoading={isContextLoading || isLoading}
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};
